import { useEffect, useState } from 'react';

/**
 * Returns `true` once the user has scrolled past `threshold` pixels.
 *
 * Used by the floating Feedback / Live-chat widgets so they stay hidden on the
 * first paint (they crowded the hero) and slide in only after the visitor
 * starts scrolling — an intentional "here if you need me" reveal.
 *
 * Falls back to visible when the document can't scroll (short pages), so the
 * widgets are never permanently unreachable.
 */
export function useRevealOnScroll(threshold = 200): boolean {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const evaluate = () => {
      const scrolled = window.scrollY > threshold;
      const notScrollable =
        document.documentElement.scrollHeight <= window.innerHeight + threshold;
      setRevealed(scrolled || notScrollable);
    };

    evaluate();
    window.addEventListener('scroll', evaluate, { passive: true });
    window.addEventListener('resize', evaluate);
    return () => {
      window.removeEventListener('scroll', evaluate);
      window.removeEventListener('resize', evaluate);
    };
  }, [threshold]);

  return revealed;
}
